import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Tag, Space, Button, message, Modal, Image } from 'antd';
import { InstagramOutlined, ReloadOutlined, EyeOutlined, SaveOutlined } from '@ant-design/icons';
import { Facebook, InstagramPost, InstagramResponse, InstagramPostDetail } from '../types/facebook';
import { FacebookService } from '../services/facebookService';
import { SocialMediaPostService } from '../services/socialMediaPostService';
import axios from 'axios';
import dayjs from 'dayjs';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';

const { Paragraph } = Typography;
const { confirm } = Modal;

const InstagramManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [facebookConfig, setFacebookConfig] = useState<Facebook | null>(null);
  const [selectedPost, setSelectedPost] = useState<InstagramPostDetail | null>(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const facebookService = new FacebookService();
  const socialMediaPostService = new SocialMediaPostService();

  const fetchFacebookConfig = async () => {
    try {
      const configs = await facebookService.getAllFacebookConfigs();
      if (configs.length > 0) {
        setFacebookConfig(configs[0]);
        return configs[0];
      }
      return null;
    } catch (error) {
      message.error('Không thể lấy cấu hình Facebook');
      return null;
    }
  };

  const fetchPosts = async (config: Facebook) => {
    setLoading(true);
    try {
      const response = await axios.get<InstagramResponse>(
        `https://graph.facebook.com/v22.0/${config.idIg}/media`,
        {
          params: {
            access_token: config.longToken
          }
        }
      );

      setPosts(response.data.data);
    } catch (error) {
      message.error('Không thể lấy danh sách bài viết Instagram');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostDetail = async (postId: string) => {
    if (!facebookConfig) return;

    try {
      setLoading(true);
      const response = await axios.get<InstagramPostDetail>(
        `https://graph.facebook.com/v22.0/${postId}`,
        {
          params: {
            fields: 'id,caption,media_type,media_url,permalink,timestamp',
            access_token: facebookConfig.longToken
          }
        }
      );
      setSelectedPost(response.data);
      setIsDetailModalVisible(true);
    } catch (error) {
      message.error('Không thể lấy chi tiết bài viết');
    } finally {
      setLoading(false);
    }
  };

  const renderPostContent = (post: InstagramPostDetail) => {
    return (
      <div className="instagram-post-detail">
        <div className="post-header" style={{ marginBottom: '24px' }}>
          <Space direction="vertical" size="small">
            <span style={{ color: '#8c8c8c' }}>
              {dayjs(post.timestamp).format('DD/MM/YYYY HH:mm:ss')}
            </span>
            {post.caption && (
              <Paragraph style={{ whiteSpace: 'pre-line', margin: 0 }}>
                {post.caption}
              </Paragraph>
            )}
          </Space>
        </div>

        <Card bordered={false}>
          <Image
            src={post.media_url}
            alt="Instagram post"
            style={{ width: '100%', borderRadius: '8px' }}
          />
        </Card>
      </div>
    );
  };

  const saveAllPosts = async () => {
    if (!facebookConfig || !posts.length) return;

    try {
      setLoading(true);
      let savedCount = 0;
      let existingCount = 0;

      for (const post of posts) {
        // Check if post already exists
        const existingPost = await socialMediaPostService.getPostByPostId(post.id);
        if (existingPost) {
          existingCount++;
          continue;
        }

        // Fetch full post details
        const response = await axios.get<InstagramPostDetail>(
          `https://graph.facebook.com/v22.0/${post.id}`,
          {
            params: {
              fields: 'id,caption,media_type,media_url,permalink,timestamp',
              access_token: facebookConfig.longToken
            }
          }
        );

        // Save to database
        await socialMediaPostService.addPost({
          type: 'instagram',
          postId: post.id,
          detail: response.data,
          createDate: new Date(response.data.timestamp)
        });
        savedCount++;
      }

      message.success(`Đã lưu ${savedCount} bài viết mới (${existingCount} bài đã tồn tại)`);
    } catch (error) {
      message.error('Có lỗi xảy ra khi lưu bài viết');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAllPosts = () => {
    confirm({
      title: 'Xác nhận lưu bài viết',
      content: 'Bạn có chắc chắn muốn lưu tất cả bài viết vào cơ sở dữ liệu không?',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      onOk: saveAllPosts
    });
  };

  useEffect(() => {
    const init = async () => {
      const config = await fetchFacebookConfig();
      if (config) {
        fetchPosts(config);
      }
    };
    init();
  }, []);

  const handleRefresh = () => {
    if (facebookConfig) {
      fetchPosts(facebookConfig);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1>Quản lý bài viết Instagram</h1>
          <Tag color="blue">{posts.length} bài viết</Tag>
        </div>

        <div className="admin-page-actions">
          <Space>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button
              type="default"
              icon={<SaveOutlined />}
              onClick={handleSaveAllPosts}
              loading={loading}
            >
              Lưu tất cả bài viết
            </Button>
          </Space>
        </div>
      </div>

      <div className="admin-table-container">
        <Table
          className="admin-table"
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id'
            },
            {
              title: 'Thao tác',
              key: 'action',
              render: (_, record) => (
                <Space>
                  <Button
                    type="link"
                    onClick={() => fetchPostDetail(record.id)}
                    icon={<EyeOutlined />}
                  >
                    Chi tiết
                  </Button>
                  <Button
                    type="link"
                    href={`https://instagram.com/p/${record.id}`}
                    target="_blank"
                    icon={<InstagramOutlined />}
                  >
                    Xem trên Instagram
                  </Button>
                </Space>
              )
            }
          ]}
          dataSource={posts}
          rowKey="id"
          loading={loading}
        />
      </div>

      <Modal
        title={
          <Space>
            <InstagramOutlined style={{ color: '#E1306C', fontSize: '20px' }} />
            <span>Chi tiết bài viết Instagram</span>
          </Space>
        }
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setSelectedPost(null);
        }}
        width={800}
        centered
        footer={[
          <Button
            key="link"
            type="primary"
            href={selectedPost?.permalink}
            target="_blank"
            icon={<InstagramOutlined />}
          >
            Xem trên Instagram
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setIsDetailModalVisible(false);
              setSelectedPost(null);
            }}
          >
            Đóng
          </Button>
        ]}
      >
        {selectedPost && renderPostContent(selectedPost)}
      </Modal>
    </AdminLayout>
  );
};

export default InstagramManagement;